import { ErrorHandler, Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'

import { LaskunAsiakas, LaskunTyypit } from '@lemon/core-firebase/model/lasku'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { SahkoisenLaskunValittajaService } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'

import { firstValueFrom } from 'rxjs'

import { LaskuService } from '../../_angular/service/lasku/lasku.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { LaskuAsiakasDialog, LaskuAsiakasDialogData, LaskuAsiakasDialogTulos } from '../dialogit/lasku-asiakas.dialog'
import { annaToimitusteksti, ToimitustavanNimet } from './asiakasrekisteri-logiikka'

@Injectable()
export class AsiakasDialogiService {
  constructor(
    private _dialog: MatDialog,
    private _laskuService: LaskuService,
    private _laskuKopioija: LaskuKopioija,
    private _translationService: LemonTranslationService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
  ) {}

  /** Palauttaa true, jos asiakas tallennettiin tai poistettiin. */
  async avaaAsiakasrekisterinAsiakas(asiakas: LaskunAsiakas | null): Promise<boolean> {
    const data: LaskuAsiakasDialogData = {
      asiakas: asiakas,
      esitaytettyNimi: '',
      tyyppi: LaskunTyypit.annaLaskunTyyppi(asiakas?.laskunTyyppi) ?? LaskunTyypit.TAVALLINEN,
      tyyppiMuokattavissa: true,
      valuutta: asiakas?.laskunValuutta ?? 'EUR',
      kieli: asiakas?.laskunKieli ?? 'fi',
      naytaPoista: !!asiakas?.avain,
    }
    const tulos = await firstValueFrom(this._dialog.open<LaskuAsiakasDialog, LaskuAsiakasDialogData, LaskuAsiakasDialogTulos | 'poistettu'>(LaskuAsiakasDialog, { data, width: '880px' }).afterClosed())
    if (!tulos) {
      return false
    }
    if (tulos === 'poistettu') {
      return true
    }

    const tallennettava = asiakas ? this._laskuKopioija.copyAsiakas(asiakas) : this._laskuKopioija.annaUusiAsiakas()
    tallennettava.nimi = tulos.nimi
    tallennettava.ytunnus = tulos.ytunnus
    tallennettava.katuosoite = tulos.katuosoite
    tallennettava.postinro = tulos.postinro
    tallennettava.postitmp = tulos.postitoimipaikka
    tallennettava.maa = tulos.maa
    tallennettava.laskunTyyppi = tulos.tyyppi?.avain ?? null
    tallennettava.laskunValuutta = tulos.valuutta
    tallennettava.laskunKieli = tulos.kieli
    tallennettava.oletusLahetystapa = tulos.oletusLahetystapa
    tallennettava.maksuaikaa = tulos.maksuaikaa
    tallennettava.sahkoinenosoite = tulos.sahkoinenosoite
    tallennettava.laskunVastaanottajat = tulos.laskunVastaanottajat
    tallennettava.asiakastyyppi = tulos.asiakastyyppi
    tallennettava.laskutusprofiiliAvain = tulos.laskutusprofiiliAvain

    this._ladataanService.aloitaLataaminen()
    try {
      await this._laskuService.updateAsiakkaat([tallennettava])
      return true
    } catch (err) {
      this._errorHandler.handleError(err)
      return false
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  annaToimitusteksti(asiakas: LaskunAsiakas): string {
    const nimet: ToimitustavanNimet = {
      verkkolasku: this._translationService.lokalisoi('lasku.asiakas-dialog.toimitustapa.verkkolasku'),
      sahkoposti: this._translationService.lokalisoi('lasku.asiakas-dialog.toimitustapa.sahkoposti'),
      itse: this._translationService.lokalisoi('lasku.asiakas-dialog.toimitustapa.itse'),
    }
    const valittajanNimi = this._sahkoisenLaskunValittajaService.annaValittajanNimi(asiakas.sahkoinenosoite?.sahkoinenValittaja) || null
    return annaToimitusteksti(asiakas, nimet, valittajanNimi)
  }
}
